import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { resolveLandingTarget } from '@/auth/resolveLandingTarget';

interface UserPreferenceRow {
  key: string;
  value: string;
}
interface PreferencesListResponse {
  items: UserPreferenceRow[];
}

/**
 * GuestGuard — wraps guest-only routes (`/`, `/login`).
 *
 * MSAL-aware variant (W1-F6-S2): redirects authenticated users away.
 * W16-F5-S2 layers the saved landing preference into the redirect:
 *   - deep-link from `location.state.from` wins,
 *   - then saved preference (read from the react-query cache when
 *     UserPreferencesProvider has already populated it),
 *   - then `/dashboard`.
 */
export interface GuestGuardProps {
  children: React.ReactNode;
}

export function GuestGuard({ children }: GuestGuardProps): JSX.Element {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  if (isAuthenticated) {
    const cached = queryClient.getQueryData<PreferencesListResponse>([
      'user-preferences',
      'all',
    ]);
    const savedLanding = cached?.items?.find((r) => r.key === 'defaultLanding')
      ?.value;
    const target = resolveLandingTarget({ location, savedLanding });
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}
